import { useState, useEffect, useCallback } from "react";
import { notification } from "antd";
import {
  safetyCheck,
  uploadProficiencySources,
  deleteProficiencySource,
  getSBEmployeeFiles,
  getSBEmployeeFileContent,
  saveSBEmployeeFiles,
  getSBEmployeeProfile,
  submitSBEmployeeFiles,
} from "Api/apiService";
import { showSbConfirm } from "Utils/sbModal";
import {
  UPLOAD_SUCCESS_STATUS,
  ERROR_MSG,
  ALERT_MSG,
  DELETE_CONFIRM,
  SUBMIT_CONFIRM,
  NOTIFICATION_DURATION,
  NOTIFICATION_PLACEMENT,
  SAVE_NOTIFICATION,
  SUBMIT_NOTIFICATION,
} from "./SbSbViewSourcesDrawerV2.constants";

// ─── Helpers ──────────────────────────────────────────────────────────────────

const mapFileList = (list) =>
  list.map((f) => ({
    filename: f.fileName,
    file_id: f.file_id,
    file_size: f.fileSize,
    uploaded_by: f.uploadedBy,
    record_insert_time_stamp: f.uploadedAt,
    file_status: f.metaData.processing_status,
  }));

const toAlert = (type, description) => ({ type, description });

// ─── Hook ─────────────────────────────────────────────────────────────────────

const useEmployeeFiles = ({ accessToken, email }) => {
  const [safetyScanning, setSafetyScanning] = useState(false);
  const [fileName, setFileName] = useState("");
  const [safetyCheckPassedFile, setSafetyCheckPassedFile] = useState(null);
  const [alertState, setAlertState] = useState(null);
  const [uploadedFiles, setUploadedFiles] = useState([]);
  const [error, setError] = useState(null);
  const [loading, setLoading] = useState(false);

  // ── Fetch file list ────────────────────────────────────────────────────────

  const fetchEmployeeFiles = useCallback(async () => {
    setLoading(true);
    setError(null);
    try {
      const list = await getSBEmployeeFiles();
      setUploadedFiles(list ? mapFileList(list) : []);
      if (!list) setError(ERROR_MSG.NO_FILES);
    } catch {
      setError(ERROR_MSG.FETCH_FILES);
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    fetchEmployeeFiles();
  }, [fetchEmployeeFiles]);

  // ── Safety check ──────────────────────────────────────────────────────────

  const runSafetyCheck = useCallback(async (file) => {
    setError(null);
    setSafetyScanning(true);
    setSafetyCheckPassedFile(false);

    const formData = new FormData();
    formData.append("email", email);
    formData.append("file", file);

    try {
      const res = await safetyCheck(formData, accessToken);
      const passed = res?.is_safe === true;
      setSafetyCheckPassedFile(passed);
      return passed;
    } catch {
      setSafetyCheckPassedFile(false);
      setError(ERROR_MSG.SAFETY_CHECK);
      return false;
    } finally {
      setSafetyScanning(false);
    }
  }, [accessToken, email]);

  // ── Upload ────────────────────────────────────────────────────────────────

  const handleFileUpload = useCallback(async (selectedFile) => {
    setError(null);
    setAlertState(null);
    setLoading(true);
    setFileName(selectedFile.name);
    setSafetyCheckPassedFile(null);

    const isSafe = await runSafetyCheck(selectedFile);
    if (!isSafe) { setLoading(false); return; }

    const formData = new FormData();
    formData.append("email", email);
    formData.append("files", selectedFile);

    try {
      const response = await uploadProficiencySources(formData, accessToken);
      if (!response || response.results[0].status !== UPLOAD_SUCCESS_STATUS) {
        throw new Error("Upload failed");
      }
      await fetchEmployeeFiles();
      setAlertState(toAlert("success", ALERT_MSG.UPLOAD_SUCCESS(selectedFile.name)));
    } catch {
      setAlertState(toAlert("error", ALERT_MSG.UPLOAD_FAIL(selectedFile.name)));
    } finally {
      setLoading(false);
    }
  }, [accessToken, email, runSafetyCheck, fetchEmployeeFiles]);

  // ── View file content ──────────────────────────────────────────────────────

  const handleFileTabClick = useCallback(async (fileId) => {
    setLoading(true);
    setError(null);
    try {
      const fileContent = await getSBEmployeeFileContent({ file_id: fileId });
      if (fileContent) {
        window.open(fileContent.signed_url, "_blank");
      } else {
        setError(ERROR_MSG.FILE_NOT_FOUND);
      }
    } catch {
      setError(ERROR_MSG.FETCH_CONTENT);
    } finally {
      setLoading(false);
    }
  }, []);

  // ── Delete ────────────────────────────────────────────────────────────────

  const handleDeleteClick = useCallback((fileId) => {
    setAlertState(null);
    const modal = showSbConfirm({
      ...DELETE_CONFIRM,
      onOk: async () => {
        modal.update({ cancelButtonProps: { disabled: true } });
        setLoading(true);
        setError(null);
        try {
          const response = await deleteProficiencySource({ file_id: fileId });
          if (response) {
            await fetchEmployeeFiles();
            setAlertState(toAlert("success", ALERT_MSG.DELETE_SUCCESS));
          } else {
            setAlertState(toAlert("error", ALERT_MSG.DELETE_FAIL));
          }
        } catch {
          setAlertState(toAlert("error", ALERT_MSG.DELETE_FAIL));
        } finally {
          setLoading(false);
        }
      },
    });
  }, [fetchEmployeeFiles]);

  // ── Save ──────────────────────────────────────────────────────────────────

  const handleSaveClick = useCallback(async () => {
    setLoading(true);
    setError(null);
    try {
      const response = await saveSBEmployeeFiles();
      if (response) {
        // await getSBEmployeeProfile();
        await fetchEmployeeFiles();
        notification.info({
          message: SAVE_NOTIFICATION.MESSAGE,
          description: SAVE_NOTIFICATION.DESCRIPTION,
          duration: NOTIFICATION_DURATION,
          placement: NOTIFICATION_PLACEMENT,
        });
      }
    } catch {
      setError(ERROR_MSG.SAVE_FILES);
    } finally {
      setLoading(false);
    }
  }, [fetchEmployeeFiles]);

  // ── Submit ────────────────────────────────────────────────────────────────

  const handleSubmitClick = useCallback(() => {
    showSbConfirm({
      ...SUBMIT_CONFIRM,
      onOk: async () => {
        setLoading(true);
        setError(null);
        try {
          const response = await submitSBEmployeeFiles();
          if (response) {
            await fetchEmployeeFiles();
            notification.success({
              message: SUBMIT_NOTIFICATION.MESSAGE,
              description: SUBMIT_NOTIFICATION.DESCRIPTION,
              duration: NOTIFICATION_DURATION,
              placement: NOTIFICATION_PLACEMENT,
            });
          } else {
            setError(ERROR_MSG.SUBMIT_FILES);
          }
        } catch {
          setError(ERROR_MSG.SUBMIT_FILES);
        } finally {
          setLoading(false);
        }
      },
    });
  }, [fetchEmployeeFiles]);

  return {
    safetyScanning,
    fileName,
    safetyCheckPassedFile,
    alertState,
    uploadedFiles,
    error,
    loading,
    handleFileUpload,
    handleFileTabClick,
    handleDeleteClick,
    handleSaveClick,
    handleSubmitClick,
  };
};

export default useEmployeeFiles;
